import {
  IonButton,
  IonButtons,
  IonCol,
  IonHeader,
  IonInput,
  IonItem,
  IonModal,
  IonRow,
  IonText,
  IonToolbar
} from '@ionic/react';
import React, { RefObject, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useVerifyTwoFactorMutation } from '@/features/crypto/cryptoApi';
import { getCryptoAmount, getCryptoWithdrawalPayload } from '../../helpers/crypto';
import { removeTags } from '../../helpers/formatters';

import Icon from '../Icon/Icon';
import styles from './CryptoWithdrawalModal.module.css';
import { getAssetName } from '@/shared/helpers/assets';
import CryptoTitle from '@/shared/components/CryptoTitle/CryptoTitle';
import { Address } from '@/types';
import { Challenge, CryptoWithdrawalPayloadBody } from '@/types/crypto';

type Props = {
  dismissModal: () => void;
  modal: RefObject<HTMLIonModalElement>;
  withdrawalData: Challenge<CryptoWithdrawalPayloadBody>;
  isOpen: boolean;
  targetAddress?: Address;
};

function isTanError(error: any) {
  try {
    return error.status == 422 && error.data.errors.length === 1 && error.data.errors[0].code == 'invalid_tan';
  } catch {
    return false;
  }
}

const CryptoWithdrawalModal: React.FC<Props> = ({ dismissModal, modal, withdrawalData, isOpen }) => {
  const { t } = useTranslation();
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [errorTextKey, setErrorTextKey] = useState<string | undefined>(undefined);

  const {
    payload: { amount, targetAddress, assetId, destinationTag, uuid }
  } = withdrawalData;

  const [verifyTwoFactorCode, { isSuccess: verifyTANSuccess, data: verifyTANData }] = useVerifyTwoFactorMutation();

  const isValidTwoFactorCode = twoFactorCode[0] !== '-' && twoFactorCode.length === 6;

  const handleError = (error: any) => {
    if (isTanError(error)) {
      setErrorTextKey('wallet.withdraw.twoFactor.error');
    } else {
      setErrorTextKey('wallet.withdraw.twoFactor.error_unknown');
    }
  };

  const handleVerifyTwoFactorCode = async () => {
    const values = {
      amount: amount,
      targetAddress,
      destinationTag
    };

    const payload = getCryptoWithdrawalPayload(values, assetId, uuid);
    const challengeId = withdrawalData?.id;

    await verifyTwoFactorCode({
      payload,
      twoFactorCode,
      challengeId
    })
      .unwrap()
      .then(() => setErrorTextKey(undefined))
      .catch((errorArg) => handleError(errorArg));
  };

  const handleClose = () => {
    dismissModal();
  };

  return (
    <>
      <IonModal ref={modal} mode="ios" isOpen={isOpen} backdropDismiss={false} className={styles.modal}>
        <IonHeader>
          <IonToolbar className={styles.toolbar}>
            <CryptoTitle assetId={assetId!} className={styles.title}>
              {getAssetName(assetId!)} {t('wallet.withdrawal')}
            </CryptoTitle>

            <IonButtons slot="end" className={styles.closeIcon}>
              <IonButton onClick={handleClose}>
                <Icon src="assets/icons/close.svg" color="primary" size="medium" />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        {verifyTANSuccess ? (
          <>
            <div className={styles.sectionCenter}>
              <div className={styles.rounded}>
                <Icon
                  size="xlLarge"
                  color={'success'}
                  src={'assets/icons/checkmark-outline.svg'}
                  className={`${styles.icon} ${styles.success}`}
                />
              </div>
            </div>

            <IonRow class="ion-justify-content-center">
              <span className={styles.withdrawalText}>{t('wallet.withdrawals.crypto.success')}</span>
            </IonRow>
            <IonRow class="ion-justify-content-center">
              <span className={styles.withdrawalDescription}>{t('wallet.withdrawals.crypto.success.info')}</span>
            </IonRow>

            <IonRow className="ion-justify-content-between">
              <IonCol size="3">
                <IonText className={styles.label}>{t('general.amount')}</IonText>
              </IonCol>
              <IonCol size="9" className="ion-text-right">
                <IonText className={styles.textRegular}>
                  {`${verifyTANData?.amount || ''} ${assetId?.toUpperCase() || ''}`}
                </IonText>
              </IonCol>
            </IonRow>

            {/*<IonRow className="ion-justify-content-between">*/}
            {/*  <IonCol size="3">*/}
            {/*    <IonText className={styles.label}>{t('wallet.withdrawals.crypto.label')}</IonText>*/}
            {/*  </IonCol>*/}
            {/*  <IonCol size="9" className="ion-text-right">*/}
            {/*    /!*{TODO: Check if the label can also be used from this endpoint}*!/*/}
            {/*    <IonText className={styles.textRegular}>{targetAddress || '-'}</IonText>*/}
            {/*  </IonCol>*/}
            {/*</IonRow>*/}

            <IonRow className="ion-justify-content-between">
              <IonCol size="3">
                <IonText className={styles.label}>{`${t('wallet.details.address')}`}</IonText>
              </IonCol>
              <IonCol size="9" className="ion-text-right">
                <IonText className={styles.textRegular}>{verifyTANData?.targetAddress}</IonText>
              </IonCol>
            </IonRow>

            {assetId === 'xrp' && (
              <IonRow className="ion-justify-content-between">
                <IonCol size="3">
                  <IonText className={styles.label}>{`${t('wallet.withdrawals.crypto.destination_tag')}`}</IonText>
                </IonCol>
                <IonCol size="9" className="ion-text-right">
                  <IonText className={styles.textRegular}>{destinationTag ? destinationTag : '-'}</IonText>
                </IonCol>
              </IonRow>
            )}
          </>
        ) : (
          <>
            <IonRow className="ion-justify-content-between">
              <IonCol size="3">
                <IonText className={styles.label}>{t('general.amount')}</IonText>
              </IonCol>
              <IonCol size="9" className="ion-text-right">
                <IonText className={styles.textRegular}>{getCryptoAmount(amount, assetId!)}</IonText>
              </IonCol>
            </IonRow>

            {/*<IonRow className="ion-justify-content-between">*/}
            {/*  <IonCol size="3">*/}
            {/*    <IonText className={styles.label}>{t('wallet.withdrawals.crypto.label')}</IonText>*/}
            {/*  </IonCol>*/}
            {/*  <IonCol size="9" className="ion-text-right">*/}
            {/*    <IonText className={styles.textRegular}>{targetAddress || '-'}</IonText>*/}
            {/*  </IonCol>*/}
            {/*</IonRow>*/}

            <IonRow className="ion-justify-content-between">
              <IonCol size="3">
                <IonText className={styles.label}>{t('wallet.details.address')}</IonText>
              </IonCol>
              <IonCol size="9" className="ion-text-right">
                <IonText className={styles.textRegular}>{targetAddress}</IonText>
              </IonCol>
            </IonRow>

            {assetId === 'xrp' && (
              <IonRow className="ion-justify-content-between">
                <IonCol size="3">
                  <IonText className={styles.label}>{t('wallet.withdrawals.crypto.destination_tag')}</IonText>
                </IonCol>
                <IonCol size="9" className="ion-text-right">
                  <IonText className={styles.textRegular}>{destinationTag ? destinationTag : '-'}</IonText>
                </IonCol>
              </IonRow>
            )}

            <div className={styles.warningWrapper}>
              <Icon
                size="xlLarge"
                color="warning"
                src={`assets/icons/warning-outline.svg`}
                className={`${styles.icon} ${styles.warning}`}
              />
              <div className={styles.warningColumn}>
                <small className={styles.warningTitle}>
                  {removeTags(t('wallet.withdraw.crypto.hint.security_check'))}
                </small>
              </div>
            </div>

            <IonRow className={styles.spacing}>
              <IonText className={`${styles.textRegular} ${styles.twoFactorHint}`}>
                <Trans i18nKey="wallet.withdraw.crypto.tan.hint" />
              </IonText>
            </IonRow>

            <IonItem className={styles.inputWrapper}>
              <IonInput
                value={twoFactorCode}
                className={styles.input}
                type="number"
                name="TwoFA-crypto"
                id="TwoFA-crypto"
                required={true}
                placeholder={t('data.twoFactor')}
                maxlength={6}
                onIonInput={(evt) => setTwoFactorCode((evt.target as unknown as HTMLInputElement).value)}
              ></IonInput>
            </IonItem>

            {errorTextKey != undefined && (
              <IonRow className={styles.spacing}>
                <IonText className={`${styles.textRegular} ${styles.twoFactorHint} ${styles.twoFactorError}`}>
                  <Trans i18nKey={errorTextKey} />
                </IonText>
              </IonRow>
            )}

            <IonRow className="ion-justify-content-between">
              <IonCol size="12">
                <IonButton
                  shape="round"
                  expand="full"
                  disabled={!isValidTwoFactorCode}
                  onClick={handleVerifyTwoFactorCode}
                >
                  {t('wallet.withdraw')}
                </IonButton>
              </IonCol>
            </IonRow>
          </>
        )}
      </IonModal>
    </>
  );
};
export default CryptoWithdrawalModal;
